import { v4 as uuidv4 } from "uuid";
import { createEmptyGroup } from "../../../../components/queryBuilder/queryBuilderUtils";
import type { ConditionFieldListOptions } from "../../../../data-model/pharmacyOrderUpdatesPaRequestsMetadata";
import type {
  PocAssignmentDoc,
  PocAssignmentEditContext,
  PocAssignmentRuleFormData,
  PocAssignmentRuleState,
} from "../../../../data-model/pocAssignmentRule";
import {
  getBooleanOperators,
  getNumericOperators,
  getStringOperators,
  getTimestampOperators,
  getUnknownDatatypeOperators,
  type FieldDefinition,
  type QueryBuilderConfig,
  type QueryBuilderValue,
  type QueryNode,
} from "../../../../data-model/queryBuilder";
import type {
  CmmInputUniqueValueItem,
  CmmInputUniqueValuesColumn,
  CmmInputUniqueValuesDoc,
} from "../validationFunnel/pages/conditionList/cmmInputUniqueValuesTypes";

const DEFAULT_LOOKBACK = "0 days";

export const DEFAULT_POC_ASSIGNMENT_DOC_ID = "poc_assignment";

/** Firestore document id under `pharmacy_order_updates/metadata` for group ↔ batch mapping. */
export const POC_ASSIGNMENT_GROUPS_DOC_ID = "poc_assignment_groups";

/** Some legacy Firestore docs spell the array `childeren` instead of `children`. Read either; always emit `children`. */
type LegacyChildrenContainer = {
  children?: QueryNode[];
  childeren?: QueryNode[];
};

/** Ensure values are string | string[] AND inject ids on groups/conditions — the Firestore doc may store them without ids, but QueryBuilder uses `id` for its change handlers and React keys. Also tolerates the `childeren` typo on input. */
function normalizeConditionForQueryBuilder(
  condition: QueryBuilderValue,
): QueryBuilderValue {
  const legacy = condition as unknown as LegacyChildrenContainer;
  const rawChildren: QueryNode[] = legacy.children ?? legacy.childeren ?? [];
  return {
    type: "group",
    id: condition.id ?? uuidv4(),
    conjunction: condition.conjunction ?? "AND",
    children: rawChildren.map((child) =>
      child.type === "condition"
        ? {
            ...child,
            id: child.id ?? uuidv4(),
            value:
              typeof child.value === "number"
                ? String(child.value)
                : Array.isArray(child.value)
                  ? child.value.map((v) =>
                      typeof v === "number" ? String(v) : v,
                    )
                  : (child.value ?? ""),
          }
        : normalizeConditionForQueryBuilder(child as QueryBuilderValue),
    ),
  };
}

/** Coerce a legacy single-string `value` into the canonical string[] used in the UI. */
function normalizeActionValue(value: string | string[] | undefined): string[] {
  if (Array.isArray(value)) return value.map((v) => String(v));
  if (value == null || value === "") return [];
  return [String(value)];
}

export function mapPocAssignmentDocToRuleStates(
  doc: PocAssignmentDoc,
): PocAssignmentRuleState[] {
  return (doc.rules ?? []).map((rule, index) => {
    const conditionKey: "rule" | "when" =
      rule.rule != null ? "rule" : rule.when != null ? "when" : "rule";
    const condition = rule.rule ?? rule.when ?? createEmptyGroup();
    return {
      id: `poc-rule-${index}-${uuidv4()}`,
      label: rule.label ?? `Rule ${index + 1}`,
      subtext: rule.subtext ?? "",
      active: rule.active ?? true,
      lookback: rule.lookback ?? DEFAULT_LOOKBACK,
      actions: (rule.actions ?? []).map((a) => ({
        type: a.type ?? "set_random",
        field: a.field ?? "",
        value: normalizeActionValue(a.value),
      })),
      triggers: (rule.triggers ?? []).map((t) => ({ ...t })),
      condition: normalizeConditionForQueryBuilder(condition),
      conditionKey,
    };
  });
}

/**
 * Applies modal form values onto a single rule state (label, triggers, actions, etc.).
 *
 * @param rule - Rule row to copy identifiers / condition from.
 * @param formData - Values from AddNewPocAssignmentModal.
 * @returns Updated rule state for React state or metadata derivation.
 */
export function applyPocAssignmentFormDataToRule(
  rule: PocAssignmentRuleState,
  formData: PocAssignmentRuleFormData,
): PocAssignmentRuleState {
  return {
    ...rule,
    label: formData.label,
    subtext: formData.subtext,
    active: formData.active,
    lookback: formData.lookback,
    actions: formData.actions.map((a) => ({
      ...a,
      value: Array.isArray(a.value) ? [...a.value] : a.value,
    })),
    triggers: formData.triggers.map((t) => ({ ...t })),
  };
}

export function mapRuleStatesToFirestorePayload(
  rules: PocAssignmentRuleState[],
  existingDoc: PocAssignmentDoc | null,
  updatedBy?: string | null,
): Pick<
  PocAssignmentDoc,
  "id" | "rules" | "created_at" | "created_by" | "updated_at" | "updated_by"
> {
  const nowIso = new Date().toISOString();
  return {
    id: existingDoc?.id ?? DEFAULT_POC_ASSIGNMENT_DOC_ID,
    rules: rules.map((r) => {
      const conditionField =
        r.conditionKey === "when"
          ? { when: r.condition }
          : { rule: r.condition };
      return {
        label: r.label,
        subtext: r.subtext ?? "",
        active: r.active,
        lookback: r.lookback,
        actions: r.actions.map((a) => ({
          type: a.type,
          field: a.field,
          value: Array.isArray(a.value) ? [...a.value] : a.value,
        })),
        triggers: r.triggers.map((t) => ({ ...t })),
        ...conditionField,
      };
    }),
    ...(existingDoc?.created_at != null && {
      created_at: existingDoc.created_at,
    }),
    ...(existingDoc?.created_by != null && {
      created_by: existingDoc.created_by,
    }),
    updated_at: nowIso,
    ...(updatedBy != null && updatedBy !== "" && { updated_by: updatedBy }),
  };
}

export function createEmptyPocAssignmentRule(
  index: number,
): PocAssignmentRuleState {
  return {
    id: `poc-rule-${uuidv4()}`,
    label: `Rule ${index + 1}`,
    subtext: "",
    active: true,
    lookback: DEFAULT_LOOKBACK,
    actions: [{ type: "set_random", field: "poc", value: [] }],
    triggers: [{ type: "order_start" }],
    condition: createEmptyGroup(),
    conditionKey: "rule",
  };
}

export const getDefaultPocAssignmentRules = (): PocAssignmentRuleState[] => [
  createEmptyPocAssignmentRule(0),
];

export function getInitialFormDataForPocAssignmentModal(
  editContext: PocAssignmentEditContext,
  rules: PocAssignmentRuleState[],
): PocAssignmentRuleFormData | undefined {
  if (editContext == null) return undefined;
  const rule = rules.find((r) => r.id === editContext.ruleId);
  if (!rule) return undefined;
  return {
    label: rule.label,
    subtext: rule.subtext ?? "",
    active: rule.active,
    lookback: rule.lookback,
    actions: rule.actions.map((a) => ({
      ...a,
      value: Array.isArray(a.value) ? [...a.value] : a.value,
    })),
    triggers: rule.triggers.map((t) => ({ ...t })),
  };
}

function topValueToOption(
  t: CmmInputUniqueValueItem | string | number | boolean,
): { value: string; label: string } {
  const isItemShape = t != null && typeof t === "object" && "value" in t;
  const value = isItemShape ? (t as CmmInputUniqueValueItem).value : t;
  const count = isItemShape ? (t as CmmInputUniqueValueItem).count : undefined;
  const valueStr = String(value ?? "").slice(0, 80);
  const suffix = String(value ?? "").length > 80 ? "…" : "";
  const countPart = count !== undefined ? ` (${count})` : "";
  return {
    value: String(value ?? ""),
    label: `${valueStr}${suffix}${countPart}`,
  };
}

function buildFieldFromCmmColumn(
  col: CmmInputUniqueValuesColumn,
): FieldDefinition {
  const valueOptionsFromTopValues =
    col.top_values?.length > 0
      ? col.top_values.slice(0, 100).map(topValueToOption)
      : undefined;

  const dt = (col.data_type ?? "").toUpperCase();
  let operators: ReturnType<typeof getStringOperators>;

  if (dt === "STRING") {
    operators = getStringOperators(valueOptionsFromTopValues ?? undefined);
  } else if (
    dt === "INTEGER" ||
    dt === "FLOAT" ||
    dt === "FLOAT64" ||
    dt === "FLOAT32" ||
    dt === "INT64" ||
    dt === "INT32"
  ) {
    operators = getNumericOperators();
  } else if (dt === "BOOLEAN") {
    operators = getBooleanOperators();
  } else if (dt === "TIMESTAMP" || dt === "DATE" || dt === "DATETIME") {
    operators = getTimestampOperators(valueOptionsFromTopValues ?? undefined);
  } else {
    operators = getUnknownDatatypeOperators(
      valueOptionsFromTopValues ?? undefined,
    );
  }

  const rawLabel = col.display_name || col.column_name.replace(/_/g, " ");
  return {
    value: col.column_name,
    label: rawLabel.charAt(0).toUpperCase() + rawLabel.slice(1),
    operators,
    dataType: col.data_type,
    valueOptions: valueOptionsFromTopValues,
  };
}

/**
 * Builds QueryBuilderConfig for the POC assignment page from the CMM input unique
 * values Firestore document.
 *
 * @param doc - CMM input unique values document from Firestore
 * @param options - When `filterByShowOnConditionList` is false, all columns are included
 * @returns Query builder config with field definitions derived from document columns
 */
export function buildPocAssignmentConditionConfigFromCmmColumns(
  doc: CmmInputUniqueValuesDoc | null,
  options?: ConditionFieldListOptions,
): QueryBuilderConfig {
  if (!doc?.columns?.length) {
    return { fields: [] };
  }
  const filterByShowOnConditionList =
    options?.filterByShowOnConditionList !== false;
  const columns = filterByShowOnConditionList
    ? doc.columns.filter((col) => col.show_on_condition_list)
    : doc.columns;
  return {
    fields: columns.map(buildFieldFromCmmColumn),
  };
}

/** POC assignment condition dropdowns include columns hidden from other config pages. */
export const POC_ASSIGNMENT_CONDITION_FIELD_OPTIONS: ConditionFieldListOptions =
  {
    filterByShowOnConditionList: false,
  };

export const POC_ASSIGNMENT_CONDITION_TITLE = "Conditions (All Must Match)";
